import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { getUserRole } from '../settings/useSettingsTabs';
import { loadStripe } from '@stripe/stripe-js';
import { paymentService } from '../../_services';
import siteSetting from '../../config/env';

const stripePromise = loadStripe(siteSetting.stripeKey);

// Stripe price ids (handles test vs live automatically via config).
const PRICE_IDS = {
  salonMonthly: siteSetting.stripe.salonMonthlyPriceId,
  salonYearly: siteSetting.stripe.salonYearlyPriceId,
  selfMonthly: siteSetting.stripe.selfMonthlyPriceId,
  selfYearly: siteSetting.stripe.selfYearlyPriceId,
};

// Display prices — mirrors ProfessionalSignup's plan step.
const PRICING = {
  salonMonthly: 59, salonYearly: 599, salonYearlyPerMonth: '49.92', salonSave: '£109',
  selfMonthly: 25, selfYearly: 249, selfYearlyPerMonth: '20.75', selfSave: '£51',
};

const DARK = '#29282D';
const ACCENT = '#007EA0';
const MUTED = '#aaa';

// Why is the user on the paywall? A fresh signup has no subscription status
// yet; a lapsed account arrives carrying the status the lifecycle webhook left
// behind. Copy + CTA adapt so it's obvious what happened and what to do next.
// (Server-side, a lapsed account gets NO second free trial — so the CTA must
// not promise one.)
function getGateContext() {
  let status = null;
  try {
    status = (JSON.parse(localStorage.getItem('subscriptionGate') || '{}').subscriptionStatus) || null;
  } catch (e) { /* treat as new */ }
  if (status === 'canceled' || status === 'incomplete_expired') {
    return {
      mode: 'ended',
      title: 'Your subscription has ended',
      subtitle: 'Choose a plan to pick up right where you left off. Your diary, clients and reports are all exactly as you left them.',
      cta: 'Subscribe now',
      note: 'You’ll be charged when you subscribe.',
    };
  }
  if (status === 'past_due' || status === 'unpaid' || status === 'paused') {
    return {
      mode: 'lapsed',
      title: 'Your free trial has ended',
      subtitle: 'We couldn’t take payment, so your account is paused. Choose a plan to keep using Beu. Nothing has been lost.',
      cta: 'Subscribe now',
      note: 'You’ll be charged when you subscribe.',
    };
  }
  return {
    mode: 'new',
    title: 'Start your subscription',
    subtitle: 'Choose your plan to continue. You won’t be charged until your 90‑day free trial ends.',
    cta: 'Start free trial',
    note: null,
  };
}

// The plan that fits the account opens first: a salon that left Stripe
// checkout comes back to Salon, not the self-employed price. Null until the
// profile has loaded.
function accountTier(user) {
  const role = getUserRole(user);
  return role === 'owner' ? 'salon' : role === 'selfEmployed' ? 'self' : null;
}
// The real saving on the plan shown (salon 15%, self-employed 17%).
const savePct = (monthly, yearly) => Math.round((1 - yearly / (monthly * 12)) * 100);

// Full-screen paywall the gate routes an unsubscribed/lapsed account to. Reuses
// the same subscriptionCheckout -> Stripe Checkout flow as the signup wizard,
// styled to match its plan step. Rendered through a portal onto document.body
// so no transformed/overflow ancestor in the app shell can trap the fixed
// overlay — it must cover EVERYTHING, side nav included.
class SubscriptionCheckout extends Component {
  constructor(props) {
    super(props);
    this.state = { planTier: accountTier(props.user) || 'self', chosen: false, billing: 'monthly', isLoading: false, error: '' };
  }

  componentDidMount() {
    if (!localStorage.getItem('accessToken')) this.props.history.push('/');
  }

  // The profile often lands after the paywall: follow it until they choose.
  componentDidUpdate(prev) {
    if (this.state.chosen || prev.user === this.props.user) return;
    const tier = accountTier(this.props.user);
    if (tier && tier !== this.state.planTier) this.setState({ planTier: tier });
  }

  getPriceId() {
    const { planTier, billing } = this.state;
    if (planTier === 'salon') return billing === 'yearly' ? PRICE_IDS.salonYearly : PRICE_IDS.salonMonthly;
    return billing === 'yearly' ? PRICE_IDS.selfYearly : PRICE_IDS.selfMonthly;
  }

  handleSubscribe = () => {
    this.setState({ isLoading: true, error: '' });
    const priceId = this.getPriceId();
    const userType = this.state.planTier === 'salon' ? 1 : 2;
    paymentService.subscriptionCheckout({ priceId, userType })
      .then(async (response) => {
        if (response.data && response.data.statusCode === 0) {
          this.setState({ isLoading: false, error: (response.data.error && response.data.error.responseMessage) || 'Could not start checkout. Please try again.' });
          return;
        }
        const sessionId = response.data && response.data.responseData && response.data.responseData.result && response.data.responseData.result.id;
        if (!sessionId) { this.setState({ isLoading: false, error: 'Could not start checkout. Please try again.' }); return; }
        const stripe = await stripePromise;
        const result = await stripe.redirectToCheckout({ sessionId });
        if (result && result.error) this.setState({ isLoading: false, error: result.error.message || 'Checkout failed. Please try again.' });
      })
      .catch(() => this.setState({ isLoading: false, error: 'Could not start checkout. Please try again.' }));
  };

  handleLogout = () => { localStorage.clear(); this.props.history.push('/'); };

  pill(active, label, onClick, extra) {
    return (
      <button type="button" onClick={onClick} style={{
        padding: '10px 22px', border: 'none', borderRadius: 10,
        background: active ? '#fff' : 'transparent', color: active ? DARK : MUTED,
        fontWeight: 600, fontSize: 14, cursor: 'pointer', transition: 'all .25s ease',
        boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
      }}>{label}{extra}</button>
    );
  }

  render() {
    const { planTier, billing, isLoading, error } = this.state;
    const isSalon = planTier === 'salon';
    const isYearly = billing === 'yearly';
    const monthlyPrice = isSalon ? PRICING.salonMonthly : PRICING.selfMonthly;
    const yearlyPerMonth = isSalon ? PRICING.salonYearlyPerMonth : PRICING.selfYearlyPerMonth;
    const yearlyTotal = isSalon ? PRICING.salonYearly : PRICING.selfYearly;
    const saveAmount = isSalon ? PRICING.salonSave : PRICING.selfSave;
    const ctx = getGateContext();

    const overlay = (
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: '#fff', zIndex: 2147483000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
          <h1 style={{ fontSize: 30, fontWeight: 700, color: DARK, letterSpacing: '-0.02em', margin: '0 0 10px' }}>{ctx.title}</h1>
          <p style={{ fontSize: 15, color: MUTED, margin: '0 0 30px', lineHeight: 1.5 }}>
            {ctx.subtitle}
          </p>

          {/* Plan tier */}
          <div style={{ display: 'inline-flex', background: '#f5f5f5', borderRadius: 12, padding: 4, marginBottom: 14 }}>
            {this.pill(!isSalon, 'Self‑employed', () => this.setState({ planTier: 'self', chosen: true }))}
            {this.pill(isSalon, 'Salon', () => this.setState({ planTier: 'salon', chosen: true }))}
          </div>
          <br />
          {/* Billing period */}
          <div style={{ display: 'inline-flex', background: '#f5f5f5', borderRadius: 12, padding: 4, marginBottom: 32 }}>
            {this.pill(!isYearly, 'Monthly', () => this.setState({ billing: 'monthly' }))}
            {this.pill(isYearly, 'Annual', () => this.setState({ billing: 'yearly' }), <span style={{ fontSize: 12, color: ACCENT, fontWeight: 600, marginLeft: 4 }}>Save {savePct(monthlyPrice, yearlyTotal)}%</span>)}
          </div>

          {/* Price */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, marginBottom: 10 }}>
            <span style={{ fontSize: 52, fontWeight: 700, color: DARK, letterSpacing: '-0.04em', lineHeight: 1 }}>£{isYearly ? yearlyPerMonth : monthlyPrice}</span>
            <span style={{ fontSize: 16, color: MUTED, fontWeight: 500 }}>/mo</span>
          </div>
          <p style={{ fontSize: 14, color: MUTED, margin: '0 0 32px', lineHeight: 1.5 }}>
            {isYearly ? `£${yearlyTotal} billed annually · Save ${saveAmount}` : 'Billed monthly, cancel any time'}
          </p>

          {error ? <p style={{ color: '#c0392b', fontSize: 14, margin: '0 0 16px' }}>{error}</p> : null}

          <button type="button" onClick={this.handleSubscribe} disabled={isLoading} style={{
            width: '100%', padding: '15px', borderRadius: 12, border: 'none', background: DARK,
            color: '#fff', fontSize: 16, fontWeight: 600, cursor: isLoading ? 'default' : 'pointer', opacity: isLoading ? 0.6 : 1,
          }}>{isLoading ? 'Starting…' : ctx.cta}</button>
          {ctx.note ? <p style={{ fontSize: 13, color: MUTED, margin: '10px 0 0' }}>{ctx.note}</p> : null}

          <div style={{ marginTop: 22 }}>
            <button type="button" onClick={this.handleLogout} style={{ background: 'none', border: 'none', color: MUTED, fontSize: 14, cursor: 'pointer' }}>Log out</button>
          </div>
        </div>
      </div>
    );

    // Portal: mount on document.body so the overlay genuinely sits above the
    // whole app (side nav included) regardless of where the route renders.
    return ReactDOM.createPortal(overlay, document.body);
  }
}

export default connect((state) => ({ user: state.user }))(SubscriptionCheckout);
